import * as React from 'react';

import type {DisplayOptionEntity} from 'nutshell-core/utils';

import type {
    ContactCreateValidationInput,
    GetContactFormValidation_contactCreateValidation_errors as FormErrorType,
} from 'nutshell-graphql-types';

import {BulletedList} from '../../../bulleted-list/bulleted-list';
import {CheckmarkButton} from '../../../button/checkmark-button/checkmark-button';
import {CloseButton} from '../../../button/close-button/close-button';
import {BaseRow} from '../../../row/base-row/base-row';
import {TextField} from '../../../textfield/textfield';

import {PotentialPeepsDropdown} from '../../potential-peeps-dropdown/potential-peeps-dropdown';

import type {ContactInput} from '../types';

import {
    FIELD_NAME,
    FIELD_EMAIL,
    FIELD_PHONE,
    FIELD_DESCRIPTION,
    FIELD_ROLE,
    FIELD_ALL,
} from './consts';
import {shouldShowPotentialContacts, shouldAllowCreate, handleKeyboardNavigation} from './helpers';
import type {ContactFormFieldTypes} from './types';

type Props = {
    temporaryContact?: ContactInput;
    onSelectContact: (arg1: DisplayOptionEntity) => void;
    onSubmit: (arg1: ContactInput) => void;
    onSearchPotentialContacts?: (arg1: ContactInput) => Promise<any>;
    onClose: () => void;
    validate: (arg1: ContactCreateValidationInput) => Promise<any>;
    hasRelatedAccounts: boolean;
    showRoleField?: boolean;
    isPotentialContactsPopover?: boolean;
};

export function ContactCardCreateable(props: Props) {
    const [name, setName] = React.useState(
        props.temporaryContact ? props.temporaryContact.name : ''
    );
    const [description, setDescription] = React.useState(
        props.temporaryContact ? props.temporaryContact.description : ''
    );
    const [phoneNumber, setPhoneNumber] = React.useState(
        props.temporaryContact ? props.temporaryContact.phoneNumber : ''
    );
    const [email, setEmail] = React.useState(
        props.temporaryContact ? props.temporaryContact.email : ''
    );
    const [role, setRole] = React.useState(
        props.temporaryContact ? props.temporaryContact.jobTitle : ''
    );
    const [potentialContacts, setPotentialContacts] = React.useState<DisplayOptionEntity[]>([]);
    const [formErrors, setFormErrors] = React.useState<FormErrorType[]>([]);

    const nameRef = React.useRef<HTMLInputElement | undefined | null>(null);
    const emailRef = React.useRef<HTMLInputElement | undefined | null>(null);
    const phoneRef = React.useRef<HTMLInputElement | undefined | null>(null);
    const descriptionRef = React.useRef<HTMLInputElement | undefined | null>(null);
    const roleRef = React.useRef<HTMLInputElement | undefined | null>(null);

    const shouldShowPotentialContactsComponent = shouldShowPotentialContacts(
        name,
        description,
        phoneNumber,
        email,
        props.hasRelatedAccounts
    );
    const shouldAllowCreateContact = shouldAllowCreate(
        Boolean(props.temporaryContact),
        name,
        phoneNumber,
        email
    );

    const popoverRef = React.useRef<HTMLElement | null | undefined | null>(null);

    React.useEffect(() => {
        if (shouldShowPotentialContactsComponent) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            props.onSearchPotentialContacts &&
                props
                    .onSearchPotentialContacts({
                        name,
                        description,
                        phoneNumber,
                        email,
                    })
                    .then((matchedContacts: DisplayOptionEntity[]) => {
                        setPotentialContacts(matchedContacts);
                    });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, description, phoneNumber, email]);

    const handleSubmitContact = () => {
        props
            .validate({
                name,
                email,
                phone: phoneNumber,
            })
            .then((res) => {
                const errors =
                    res &&
                    res.data &&
                    res.data.contactCreateValidation &&
                    res.data.contactCreateValidation.errors;

                if (errors && errors.length > 0) {
                    setFormErrors(errors);

                    return;
                }
                setFormErrors([]);
                props.onSubmit({
                    name,
                    description,
                    phoneNumber,
                    email,
                    jobTitle: role,
                });
            });
    };

    const handleEnter = (e: React.KeyboardEvent) => {
        e.preventDefault();
        handleSubmitContact();
    };

    const getFieldHasError = (field: ContactFormFieldTypes) => {
        if (formErrors.length === 0) {
            return false;
        }

        return (
            formErrors &&
            formErrors.some((error) => {
                if (error.field === FIELD_ALL) {
                    return true;
                } else if (error.field === field) {
                    return true;
                }

                return false;
            })
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent, currentField: ContactFormFieldTypes) => {
        handleKeyboardNavigation(
            e,
            Boolean(props.showRoleField),
            currentField,
            emailRef,
            phoneRef,
            descriptionRef,
            roleRef,
            nameRef
        );
    };

    return (
        <>
            <BaseRow type='contacts' getRowRef={popoverRef}>
                <div className='flex flex-dir-col gap-8'>
                    <div className='flex gap-8'>
                        <div className='flex flex-dir-col gap-8 full-width'>
                            <TextField
                                placeholder='Name'
                                value={name}
                                onChange={(val) => {
                                    setName(val);
                                    setFormErrors([]);
                                }}
                                // @ts-expect-error
                                autoFocus={true}
                                size='small'
                                ignorePasswordAutoFill={true}
                                onKeyDown={(e) => handleKeyDown(e, FIELD_NAME)}
                                onEnter={handleEnter}
                                textFieldRef={nameRef}
                                hasError={getFieldHasError(FIELD_NAME)}
                            />
                            <TextField
                                placeholder='Description'
                                value={description || ''}
                                onChange={(val) => {
                                    setDescription(val);
                                    setFormErrors([]);
                                }}
                                size='small'
                                ignorePasswordAutoFill={true}
                                onKeyDown={(e) => handleKeyDown(e, FIELD_DESCRIPTION)}
                                onEnter={handleEnter}
                                textFieldRef={descriptionRef}
                            />
                            {props.showRoleField && (
                                <TextField
                                    placeholder='Role'
                                    value={role || ''}
                                    onChange={(val) => {
                                        setRole(val);
                                        setFormErrors([]);
                                    }}
                                    size='small'
                                    ignorePasswordAutoFill={true}
                                    onKeyDown={(e) => handleKeyDown(e, FIELD_ROLE)}
                                    onEnter={handleEnter}
                                    textFieldRef={roleRef}
                                />
                            )}
                            <TextField
                                placeholder='Phone number'
                                value={phoneNumber || ''}
                                onChange={(val) => {
                                    setPhoneNumber(val);
                                    setFormErrors([]);
                                }}
                                size='small'
                                ignorePasswordAutoFill={true}
                                onKeyDown={(e) => handleKeyDown(e, FIELD_PHONE)}
                                onEnter={handleEnter}
                                textFieldRef={phoneRef}
                                hasError={getFieldHasError(FIELD_PHONE)}
                            />
                            <TextField
                                placeholder='Email'
                                value={email || ''}
                                onChange={(val) => {
                                    setEmail(val);
                                    setFormErrors([]);
                                }}
                                size='small'
                                ignorePasswordAutoFill={true}
                                onKeyDown={(e) => handleKeyDown(e, FIELD_EMAIL)}
                                onEnter={handleEnter}
                                textFieldRef={emailRef}
                                hasError={getFieldHasError(FIELD_EMAIL)}
                            />
                        </div>
                        <div className='flex flex-dir-col align-center gap-8'>
                            {props.temporaryContact && (
                                <CheckmarkButton
                                    className='no-padding'
                                    // @ts-expect-error
                                    size={12}
                                    onClick={handleSubmitContact}
                                    variant='dark'
                                />
                            )}
                            <CloseButton className='no-padding' size={12} onClick={props.onClose} />
                        </div>
                    </div>
                    {formErrors && formErrors.length > 0 && (
                        <BulletedList
                            bullets={formErrors.map((error) => error.error)}
                            variant='errors'
                            size='small'
                            marginBottom={8}
                        />
                    )}
                </div>
            </BaseRow>
            {shouldShowPotentialContactsComponent && (
                <PotentialPeepsDropdown
                    potentialPeeps={potentialContacts}
                    onSelectPeep={props.onSelectContact}
                    popoverRef={popoverRef}
                    isPopover={props.isPotentialContactsPopover}
                    createButtonProps={{
                        shouldShow: Boolean(shouldAllowCreateContact),
                        onCreate: handleSubmitContact,
                        entityName: name,
                        entityType: 'person',
                    }}
                />
            )}
        </>
    );
}
